import { Close } from "@mui/icons-material"
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import { Document, Page, PDFViewer, StyleSheet, Text, View } from "@react-pdf/renderer"
import { DataType, DeviceEutGroup } from "graphql/types"
import { formatNumberForLocale } from "utils/numberInputHelpers"

type DeliveryBillDialogProps = {
  open: boolean
  onClose: () => void
  eut: DeviceEutGroup
}

// PDF Styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
    fontSize: 12,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
    borderBottom: "1px solid #000",
    paddingBottom: 10,
  },
  headerLeft: {
    flexDirection: "column",
  },
  headerRight: {
    flexDirection: "column",
    alignItems: "flex-end",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    borderBottom: "1px solid #ccc",
    paddingBottom: 5,
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    fontWeight: "bold",
    width: "30%",
  },
  value: {
    width: "70%",
  },
  table: {
    width: "100%",
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #ccc",
    paddingVertical: 5,
  },
  tableHeader: {
    fontWeight: "bold",
    backgroundColor: "#f0f0f0",
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 5,
  },
  imageContainer: {
    marginTop: 10,
    alignItems: "center",
  },
  image: {
    width: 200,
    height: 150,
    objectFit: "contain",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "1px solid #000",
    paddingTop: 10,
  },
  pageNumber: {
    fontSize: 10,
  },
})

// PDF Document Component
const DeliveryBillPDF = ({ eut }: { eut: DeviceEutGroup }) => {
  const currentDate = new Date().toLocaleDateString("de-DE")
  const currentTime = new Date().toLocaleTimeString("de-DE")

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>Lieferschein</Text>
            <Text>EUT: {eut.name}</Text>
            <Text>Barcode: {eut.barcode}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text>Datum: {currentDate}</Text>
            <Text>Uhrzeit: {currentTime}</Text>
          </View>
        </View>

        {/* EUT Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>EUT Informationen</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{eut.name}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Barcode:</Text>
            <Text style={styles.value}>{eut.barcode}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Beschreibung:</Text>
            <Text style={styles.value}>{eut.description}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Annahmen:</Text>
            <Text style={styles.value}>{eut.assumptions}</Text>
          </View>
        </View>

        {/* Device Data Groups */}
        {eut.deviceDataGroup && eut.deviceDataGroup.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Gerätedaten</Text>
            {eut.deviceDataGroup.map((group, groupIndex) => (
              <View key={groupIndex} style={{ marginBottom: 15 }}>
                <Text style={{ fontWeight: "bold", marginBottom: 5 }}>{group.name}</Text>
                <View style={styles.table}>
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={styles.tableCell}>Parameter</Text>
                    <Text style={styles.tableCell}>Wert</Text>
                    <Text style={styles.tableCell}>Einheit</Text>
                  </View>
                  {group.deviceData.map((data, dataIndex) => {
                    const entries = data.data || []
                    const formattedValues = entries
                      .map((entry) => {
                        if (
                          entry.value === null ||
                          entry.value === undefined ||
                          entry.value === ""
                        ) {
                          return ""
                        }

                        if (data.dataType === DataType.FLOAT) {
                          return formatNumberForLocale(entry.value as number | string)
                        }

                        return entry.value.toString()
                      })
                      .filter(Boolean)
                      .join(", ")

                    const formattedUnits = entries
                      .map((entry) => entry.unit || "")
                      .filter(Boolean)
                      .join(", ")

                    return (
                      <View key={dataIndex} style={styles.tableRow}>
                        <Text style={styles.tableCell}>{data.name}</Text>
                        <Text style={styles.tableCell}>{formattedValues}</Text>
                        <Text style={styles.tableCell}>{formattedUnits}</Text>
                      </View>
                    )
                  })}
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Individual Devices */}
        {eut.devices && eut.devices.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Einzelgeräte</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCell}>Gerät</Text>
                <Text style={styles.tableCell}>Barcode</Text>
                <Text style={styles.tableCell}>Notizen</Text>
              </View>
              {eut.devices.map((device, deviceIndex) => (
                <View key={deviceIndex} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{device.name}</Text>
                  <Text style={styles.tableCell}>{device.barcode}</Text>
                  <Text style={styles.tableCell}>
                    {device.note && device.note.length > 0 ? device.note.join(", ") : ""}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Movement Data */}
        {eut.movementData && eut.movementData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Bewegungsdaten</Text>
            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCell}>Datum</Text>
                <Text style={styles.tableCell}>Typ</Text>
                <Text style={styles.tableCell}>Versandnummer</Text>
                <Text style={styles.tableCell}>Transporteur</Text>
                <Text style={styles.tableCell}>Standort</Text>
                <Text style={styles.tableCell}>Notiz</Text>
              </View>
              {eut.movementData.map((movement, movementIndex) => (
                <View key={movementIndex} style={styles.tableRow}>
                  <Text style={styles.tableCell}>
                    {new Date(movement.date).toLocaleDateString("de-DE")}
                  </Text>
                  <Text style={styles.tableCell}>{movement.type}</Text>
                  <Text style={styles.tableCell}>{movement.shipmentNumber}</Text>
                  <Text style={styles.tableCell}>{movement.carrier}</Text>
                  <Text style={styles.tableCell}>{movement.location}</Text>
                  <Text style={styles.tableCell}>{movement.note}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            Erstellt am: {currentDate} um {currentTime}
          </Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Seite ${pageNumber} von ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  )
}

export const DeliveryBillDialog = ({ open, onClose, eut }: DeliveryBillDialogProps) => {
  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Lieferschein - {eut.name}</Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ height: "80vh", p: 0 }}>
        <PDFViewer width="100%" height="100%">
          <DeliveryBillPDF eut={eut} />
        </PDFViewer>
      </DialogContent>
    </Dialog>
  )
}
